import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ErrorState, Spinner } from '../components/ui.jsx';
import { useFetch, useTitle } from '../lib/hooks.js';
import { formatDate, formatXaf } from '../lib/format.js';
import { saveElementAsPdf } from '../lib/pdf.js';
import { useMeta } from '../state/MetaContext.jsx';
import { useToast } from '../state/ToastContext.jsx';

/**
 * The sale agreement for an accepted offer, on the seller's letterhead and laid
 * out for A4. It is printed and signed by hand before two witnesses; ownership
 * itself still passes by a notarial deed, and the agreement says so plainly.
 */
const Line = ({ label }) => (
  <div className="sale-sign-line">
    <span className="sale-sign-rule" />
    <span className="muted">{label}</span>
  </div>
);

const SignatureBox = ({ title, name }) => (
  <div className="signature-block sale-signature">
    <dt>{title}</dt>
    <dd>
      <strong>{name || ' '}</strong>
      <Line label="Signature" />
      <Line label="Date" />
    </dd>
  </div>
);

const PrintableSaleAgreement = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { propertyTypeLabel, landTitleLabel, landUseLabel, financeLabel } = useMeta();
  const { data, loading, error, reload } = useFetch(`/offers/${id}/agreement`, [id]);
  const sheetRef = useRef(null);
  const [saving, setSaving] = useState(false);

  useTitle(data ? `Sale agreement ${data.agreement.reference}` : 'Sale agreement');

  useEffect(() => {
    document.body.classList.add('printing');
    return () => document.body.classList.remove('printing');
  }, []);

  const downloadPDF = async () => {
    if (!sheetRef.current || !data || saving) return;
    setSaving(true);
    try {
      await saveElementAsPdf(sheetRef.current, {
        margin: 10,
        filename: `sale-agreement-${data.agreement.reference}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      });
    } catch (cause) {
      console.error('Sale agreement PDF failed:', cause);
      toast.error('Could not build the PDF. Use the Print button and save as PDF instead.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner />;
  if (error) {
    return (
      <div className="container section">
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  }

  const { agreement } = data;
  const { seller, buyer, property, parcel } = agreement;
  const company = seller.businessName || seller.name;
  const location = [property.address, property.neighbourhood, property.city].filter(Boolean).join(', ');
  const plot = parcel
    ? [
        parcel.plotNumber ? `Plot ${parcel.plotNumber}` : null,
        parcel.layout ? `${parcel.layout} layout` : null,
        `${parcel.village}${parcel.city ? `, ${parcel.city}` : ''}`
      ]
        .filter(Boolean)
        .join(', ')
    : null;
  const area = parcel?.areaSqm || property.plotSqm;
  const certificate = parcel?.certificateNumber
    ? `Land certificate (titre foncier) No. ${parcel.certificateNumber}`
    : landTitleLabel(property.landTitle) || 'Not stated';
  const isLand = property.type === 'land';

  return (
    <div className="print-page">
      <div className="print-toolbar">
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
          Back
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => window.print()}>
          Print
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={downloadPDF} disabled={saving}>
          {saving ? 'Preparing PDF...' : '↓ Download PDF'}
        </button>
        <span className="muted">Print two copies: one for the seller, one for the buyer. Both sign both.</span>
      </div>

      <article ref={sheetRef} className="lease-sheet">
        <div className="lease-document is-print sale-agreement">
          <header className="lease-doc-head">
            <div>
              {seller.logoUrl ? <img className="doc-logo" src={seller.logoUrl} alt="" /> : null}
              {company ? <p className="lease-letterhead">{company}</p> : null}
              <h2>Agreement of sale</h2>
              <p className="muted">
                Reference {agreement.reference}
                {agreement.platform?.name ? ` - prepared through ${agreement.platform.name}` : ''}
                {agreement.acceptedOn ? ` - offer accepted ${formatDate(agreement.acceptedOn)}` : ''}
              </p>
            </div>
          </header>

          <p>
            This agreement is made between the seller and the buyer named below, for the sale of the
            {isLand ? ' land' : ' property'} described in it, at the price and on the terms set out here.
          </p>

          <dl className="lease-parties">
            <div>
              <dt>The seller</dt>
              <dd>
                {seller.name}
                {seller.businessName && seller.businessName !== seller.name ? <span className="muted"> ({seller.businessName})</span> : null}
                {seller.phone ? <span className="muted"> {seller.phone}</span> : null}
                {seller.email ? <span className="muted">, {seller.email}</span> : null}
              </dd>
            </div>
            <div>
              <dt>The buyer</dt>
              <dd>
                {buyer.name}
                {buyer.phone ? <span className="muted"> {buyer.phone}</span> : null}
                {buyer.email ? <span className="muted">, {buyer.email}</span> : null}
              </dd>
            </div>
          </dl>

          <h3 className="sale-heading">1. The {isLand ? 'land' : 'property'}</h3>
          <dl className="lease-parties">
            <div>
              <dt>Description</dt>
              <dd>
                {property.title}
                {property.type ? <span className="muted"> - {propertyTypeLabel(property.type)}</span> : null}
              </dd>
            </div>
            {location ? (
              <div>
                <dt>Location</dt>
                <dd>{location}</dd>
              </div>
            ) : null}
            {plot ? (
              <div>
                <dt>Plot</dt>
                <dd>{plot}</dd>
              </div>
            ) : null}
            {area ? (
              <div>
                <dt>Area</dt>
                <dd>About {Number(area).toLocaleString('en').replace(/,/g, ' ')} square metres</dd>
              </div>
            ) : null}
            <div>
              <dt>Title</dt>
              <dd>{certificate}</dd>
            </div>
            {property.landUse ? (
              <div>
                <dt>Use</dt>
                <dd>{landUseLabel(property.landUse)}</dd>
              </div>
            ) : null}
            {parcel ? (
              <div>
                <dt>Land register</dt>
                <dd>
                  Registered on {agreement.platform?.name || 'the platform'} as plot no. {parcel.id}
                  {parcel.verified ? ', checked by its administrators' : ', not yet checked'}. Site plan signed by{' '}
                  {parcel.chiefName}.
                </dd>
              </div>
            ) : null}
          </dl>

          <h3 className="sale-heading">2. The price</h3>
          <dl className="lease-parties">
            <div>
              <dt>Agreed price</dt>
              <dd>
                <strong>{formatXaf(agreement.amount)}</strong>
                <span className="muted"> ({agreement.amountInWords} CFA)</span>
              </dd>
            </div>
            <div>
              <dt>To be paid</dt>
              <dd>{financeLabel(agreement.finance)}</dd>
            </div>
          </dl>

          <h3 className="sale-heading">3. Terms</h3>
          <ol className="lease-terms sale-terms">
            <li>
              The seller agrees to sell, and the buyer agrees to buy, the {isLand ? 'land' : 'property'} described above for
              the agreed price.
            </li>
            <li>
              The seller declares that they are its owner, that they have the right to sell it, and that to their knowledge it
              is not subject to any other sale, promise of sale, charge, claim or dispute.
            </li>
            <li>
              The buyer pays the price as set out above. Every payment is made against a written receipt signed by the seller
              and is counted towards the price. The buyer is entitled to see the original title documents and site plan before
              paying.
            </li>
            <li>
              The parties will complete the transfer of ownership before a notary and register it with the land registry. The
              seller hands over the original land certificate or site plan and every document needed for the transfer. The
              notary's and registration fees are shared as the parties agree below; where they agree nothing, they are borne
              by the buyer.
            </li>
            <li>
              The buyer takes possession once the price has been paid in full and the deed of sale has been signed, unless the
              parties agree otherwise below.
            </li>
            <li>
              If the sale cannot be completed because of a fault in the seller's title, the seller refunds everything the
              buyer has paid. If the buyer withdraws without good reason, any refund is as agreed below.
            </li>
            <li>
              This agreement records what the parties have agreed. It does not by itself transfer ownership, which passes by
              the notarial deed and its registration. Any dispute is settled amicably first, and failing that before the
              competent courts of {parcel?.city || property.city || 'the place where the property lies'}.
            </li>
          </ol>

          <h3 className="sale-heading">4. Anything else agreed</h3>
          <div className="sale-blank-lines">
            <span />
            <span />
            <span />
          </div>

          <p className="sale-signed-at">
            Signed at <span className="sale-fill" /> on <span className="sale-fill" />, in two copies, one for each party.
          </p>

          <dl className="lease-signatures sale-signatures">
            <SignatureBox title="The seller" name={seller.name} />
            <SignatureBox title="The buyer" name={buyer.name} />
            <SignatureBox title="First witness" />
            <SignatureBox title="Second witness" />
          </dl>

          {agreement.completedOn ? (
            <p className="muted">The seller marked this sale complete on {formatDate(agreement.completedOn)}.</p>
          ) : null}
          <p className="muted sale-footnote">
            Prepared through {agreement.platform?.name || 'Cameroon Homes'}, which introduced the buyer to the seller. It
            takes no part in the payment or the conveyance.
            {agreement.platform?.support ? ` Support: ${agreement.platform.support}` : ''}
          </p>
        </div>
      </article>
    </div>
  );
};

export default PrintableSaleAgreement;
